/**
 * TransferDialog — shared modal shell for Import and Export experiences.
 *
 * Enforces the visual contract from the plan:
 * - Consistent dialog width (max 960 px on desktop)
 * - Stable header / step-indicator / footer layout
 * - Full-screen on mobile viewports
 * - Focus trap and return-focus on close
 */
import { useId } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export interface TransferDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Rendered in the header area after the title/description row. */
  stepIndicator?: React.ReactNode;
  /** Dialog content. */
  children: React.ReactNode;
  /** Action row rendered in the sticky footer. */
  footer: React.ReactNode;
  'data-testid'?: string;
}

const DIALOG_MAX_WIDTH = 960;

export function TransferDialog({
  open,
  onClose,
  title,
  description,
  stepIndicator,
  children,
  footer,
  'data-testid': testId,
}: TransferDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const titleId = useId();
  const descId = useId();

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => { if (reason !== 'backdropClick') onClose(); }}
      fullScreen={isFullScreen}
      maxWidth="lg"
      fullWidth
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      slotProps={{
        paper: {
          sx: {
            maxWidth: DIALOG_MAX_WIDTH,
            height: isFullScreen ? '100%' : 'auto',
            maxHeight: isFullScreen ? '100%' : '90vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: isFullScreen ? 0 : 2,
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: stepIndicator ? 0 : 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id={titleId} variant="h6" component="h2" sx={{ fontWeight: 700, mb: 0.25 }}>
            {title}
          </Typography>
          {description && (
            <Typography id={descId} variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          aria-label={t('common:close')}
          size="small"
          sx={{ mt: -0.5, flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {stepIndicator && (
        <Box sx={{ px: 3, pt: 2 }}>
          {stepIndicator}
        </Box>
      )}

      <Divider sx={{ mt: stepIndicator ? 2 : 0 }} />

      {/* Scrollable content */}
      <DialogContent sx={{ flex: 1, overflowY: 'auto', p: 3, minHeight: 0 }}>
        {children}
      </DialogContent>

      <Divider />

      {/* Sticky footer */}
      <Box
        component="footer"
        sx={{
          px: 3,
          py: 2,
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          '& .MuiButton-root': {
            minWidth: { xs: '100%', sm: 'auto' },
          },
        }}
      >
        {footer}
      </Box>
    </Dialog>
  );
}
